// XXX: HARD RULE -- WHAT IS IN CONTEXT.
// XXX: An author's only options are the items already in context for the current
// XXX: DC: for a PAGE that is exactly its provides-contexts; for a COMPONENT that
// XXX: is provides-contexts PLUS required-contexts. Nothing else is ever in
// XXX: context. Standard/ambient entities (currentUser, resumeStory,
// XXX: configuration, appUser, hostStandardContextRoots) are NOT implicitly
// XXX: available: they reach a DC only when the author explicitly provides them
// XXX: with entitySource "session". A PROVIDING picker on the session source may
// XXX: list them; a CONSUMING picker (URL variables, element bindings,
// XXX: required-context prompts) must NOT, ever.
// XXX: Offering a context the DC does not have lets an author save a binding that
// XXX: can never resolve, which renders empty at runtime with no clue why.
// XXX: Every consuming picker must share ONE filtered candidate builder --
// XXX: per-modal lists are how ambient entries leak back in.
// XXX: See docs/CONTEXT_AVAILABILITY.md.
// Enumerates the provide-context modal's "standard entities": current user,
// then per app (dependencies recursed, cycle-safe). CJS on purpose (node unit suite).

const KEY_CURRENT_USER = "currentUser";
const KEY_APP_CONFIGURATION = "appConfiguration";
const KEY_APP_USER = "appUser";
// Ambient, app-less like currentUser: the story this viewer most recently
// opened, so a card can say "pick up where you left off" on any page.
// XXX: The KEY stays "resumeStory" -- it is persisted on every provides-contexts
// XXX: entry that binds it, so renaming it is a data migration, not a rename.
// XXX: Only the LABEL is author-facing, and that is what says what it means.
const KEY_RESUME_STORY = "resumeStory";
// Ambient, app-less like currentUser: the profile half of the signed-in user.
// It is a SEPARATE entity from User because the engine's standard User is
// app-agnostic and app authors cannot add fields to it -- see task #106.
// Keyed by the same user id, so it resolves off the same session pk.
const KEY_CURRENT_USER_PROFILE = "currentUserProfile";
const KEY_CURRENT_SUBSCRIPTION = "currentSubscription";
// The viewer's own access groups (task 224), keyed by the session user id like the profile.
const KEY_CURRENT_USER_ACCESS = "currentUserAccess";
// Ambient, app-less like resumeStory: the program item this viewer should pick
// up next, and the program it belongs to. Empty until they make progress.
const KEY_CONTINUE_ACTIVITY = "continueActivity";
const KEY_CONTINUE_PROGRAM = "continueProgram";

const LABEL_CURRENT_USER = "Current User";
const LABEL_LAST_STORY_READ = "Last Story User Read";
const LABEL_CURRENT_USER_PROFILE = "Current User Profile";
const LABEL_CURRENT_SUBSCRIPTION = "Current Subscription";
const LABEL_CURRENT_USER_ACCESS = "Current User Access";
const LABEL_CONTINUE_ACTIVITY = "Continue Activity";
const LABEL_CONTINUE_PROGRAM = "Continue Program";
const DEFAULT_CONFIGURATION_ENTITY_TYPE = "Configuration";
const DEFAULT_USER_ENTITY_TYPE = "User";
const RESUME_STORY_ENTITY_TYPE = "Story";
const USER_PROFILE_ENTITY_TYPE = "UserProfile";
const SUBSCRIPTION_ENTITY_TYPE = "Subscription";
const USER_ACCESS_ENTITY_TYPE = "UserAccess";

class StandardContextEntities {

  /** ******************* Public Static Methods *********************/

  // sources: registered Context sources keyed by appName; catalog: the
  // window.blclient.data.dbApps payload, the fallback for unregistered deps.
  // XXX: RULED 2026-08-26 -- a platform entity is an ENTITY TYPE, not an ambient
  // XXX: context. It is pickable under source=constant / source=uri via
  // XXX: ContextSourceOptions.buildEntityTypeOptions, and it appears in a Context /
  // XXX: Context Field list ONLY when the DC declares a context of that type. It is
  // XXX: NEVER a Session row, so enumerate does not take hostEntities at all --
  // XXX: there is nothing to filter because there is nothing to produce.
  // XXX: These rows are the ambient entities behind a PAGE's Session source, and
  // XXX: that is the only place they belong. They are never candidates for a
  // XXX: consuming picker -- not by being filtered out of one, but because a
  // XXX: consuming picker's list comes from declaredContextList, which reads only
  // XXX: the DC's own required/provides contexts and cannot produce them.
  // allow: the App Builder's always-in-session values (AppBuilderContextScope). Absent in a destination editor.
  static enumerate({ sources, catalog, allow = null }) {
    // Display contract (shared with ContextSourceOptions): label
    // "<appTitle> - <name>", entity type as the subtle secondaryLabel.
    const rows = [{
      value: KEY_CURRENT_USER,
      label: LABEL_CURRENT_USER,
      secondaryLabel: DEFAULT_USER_ENTITY_TYPE,
      entityType: DEFAULT_USER_ENTITY_TYPE,
    }, {
      value: KEY_RESUME_STORY,
      label: LABEL_LAST_STORY_READ,
      secondaryLabel: RESUME_STORY_ENTITY_TYPE,
      entityType: RESUME_STORY_ENTITY_TYPE,
      standardEntity: KEY_RESUME_STORY,
    }, {
      value: KEY_CURRENT_USER_PROFILE,
      label: LABEL_CURRENT_USER_PROFILE,
      secondaryLabel: USER_PROFILE_ENTITY_TYPE,
      entityType: USER_PROFILE_ENTITY_TYPE,
      standardEntity: KEY_CURRENT_USER_PROFILE,
    }, {
      value: KEY_CURRENT_SUBSCRIPTION,
      label: LABEL_CURRENT_SUBSCRIPTION,
      secondaryLabel: SUBSCRIPTION_ENTITY_TYPE,
      entityType: SUBSCRIPTION_ENTITY_TYPE,
      standardEntity: KEY_CURRENT_SUBSCRIPTION,
    }, {
      value: KEY_CURRENT_USER_ACCESS,
      label: LABEL_CURRENT_USER_ACCESS,
      secondaryLabel: USER_ACCESS_ENTITY_TYPE,
      entityType: USER_ACCESS_ENTITY_TYPE,
      standardEntity: KEY_CURRENT_USER_ACCESS,
    }, {
      value: KEY_CONTINUE_ACTIVITY,
      label: LABEL_CONTINUE_ACTIVITY,
      secondaryLabel: RESUME_STORY_ENTITY_TYPE,
      entityType: RESUME_STORY_ENTITY_TYPE,
      standardEntity: KEY_CONTINUE_ACTIVITY,
    }, {
      value: KEY_CONTINUE_PROGRAM,
      label: LABEL_CONTINUE_PROGRAM,
      secondaryLabel: RESUME_STORY_ENTITY_TYPE,
      entityType: RESUME_STORY_ENTITY_TYPE,
      standardEntity: KEY_CONTINUE_PROGRAM,
    }];
    const visited = new Set();
    const appRows = [];
    for (const appName of Object.keys(sources ?? {})) {
      StandardContextEntities.#walkApp(appName, sources, catalog, visited, appRows);
    }
    if (!allow) {
      return [...rows, ...appRows];
    }
    // A leaked app row is a scoping bug to see, not hide: it would let a template depend on an app it doesn't ship with.
    const leaked = appRows.filter(row => !allow.includes(row.value));
    if (leaked.length) {
      throw new Error(`App Builder session rows outside the app's scope: ${leaked.map(row => row.value).join(", ")}.`
        + ` Allowed: ${allow.join(", ")}. Only the app and its dependencies may be context sources here.`);
    }
    return [...rows.filter(row => allow.includes(row.value)), ...appRows];
  }

  /** ******************* Private Static Methods *********************/

  static #walkApp(appName, sources, catalog, visited, rows) {
    if (visited.has(appName)) {
      return;
    }
    visited.add(appName);
    const appConfig = StandardContextEntities.#resolveAppConfig(appName, sources, catalog);
    if (!appConfig) {
      return;
    }
    rows.push({
      value: `${KEY_APP_CONFIGURATION}:${appName}`,
      label: `${appConfig.title} - App Configuration`,
      secondaryLabel: appConfig.configurationEntity,
      entityType: appConfig.configurationEntity,
      appName,
      standardEntity: KEY_APP_CONFIGURATION,
    });
    if (appConfig.appUser?.entityType) {
      rows.push({
        value: `${KEY_APP_USER}:${appName}`,
        label: `${appConfig.title} - App User`,
        secondaryLabel: appConfig.appUser.entityType,
        entityType: appConfig.appUser.entityType,
        appName,
        standardEntity: KEY_APP_USER,
      });
    }
    for (const dependencyName of appConfig.dependencies) {
      StandardContextEntities.#walkApp(dependencyName, sources, catalog, visited, rows);
    }
  }

  // A registered source wins; the catalog covers unregistered dependency
  // names; an app found in neither is skipped.
  static #resolveAppConfig(appName, sources, catalog) {
    const source = sources?.[appName];
    if (source) {
      // Already normalized at its own boundary (buildDbAppContextSource) —
      // trust it, don't re-default.
      return source;
    }
    const catalogEntry = catalog?.[appName];
    if (catalogEntry) {
      return {
        title: catalogEntry.title ?? appName,
        dependencies: catalogEntry.config?.dependencies ?? [],
        appUser: catalogEntry.config?.appUser ?? null,
        configurationEntity: catalogEntry.config?.configurationEntity ?? DEFAULT_CONFIGURATION_ENTITY_TYPE,
      };
    }
    return null;
  }

}

module.exports = StandardContextEntities;
